import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SOLUTIONS } from '../../data/solutions';

const linkClass = ({ isActive }) =>
    `px-2 py-2 font-bold transition-colors duration-200 ${isActive ? 'text-primary' : 'text-gray-600 hover:text-primary'}`;

// Desktop dropdown + plain links. On mobile (inside the burger panel) the
// dropdown renders as an expanded list instead.
const NavLinks = ({ isMobile = false, onNavigate = () => {} }) => {
    const { t } = useTranslation();
    const [solutionsOpen, setSolutionsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (isMobile) return;
        const close = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setSolutionsOpen(false);
            }
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [isMobile]);

    const solutionLinks = SOLUTIONS.map((s) => (
        <Link
            key={s.slug}
            to={`/solutions/${s.slug}`}
            onClick={() => { setSolutionsOpen(false); onNavigate(); }}
            className={isMobile
                ? 'block py-1.5 text-gray-600 hover:text-primary text-sm'
                : 'flex items-start px-4 py-2.5 hover:bg-gray-50 text-gray-700 hover:text-primary text-sm font-semibold'}
        >
            {t(`solutions.${s.key}.title`)}
        </Link>
    ));

    if (isMobile) {
        return (
            <>
                <button
                    onClick={() => setSolutionsOpen(!solutionsOpen)}
                    className="px-3 py-2 font-bold text-gray-600 hover:text-primary inline-flex items-center justify-center"
                    aria-expanded={solutionsOpen}
                >
                    {t('nav.solutions')}
                    <svg className={`w-4 h-4 ml-1 transition-transform ${solutionsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {solutionsOpen && (
                    <div className="pb-2">
                        <Link to="/solutions" onClick={onNavigate} className="block py-1.5 text-primary font-bold text-sm">
                            {t('common.all_solutions')}
                        </Link>
                        {solutionLinks}
                    </div>
                )}
                <NavLink className={linkClass} to="/technology" onClick={onNavigate}>{t('nav.technology')}</NavLink>
                <NavLink className={linkClass} to="/industries" onClick={onNavigate}>{t('nav.industries')}</NavLink>
                <NavLink className={linkClass} to="/case-studies" onClick={onNavigate}>{t('nav.case_studies')}</NavLink>
                <NavLink className={linkClass} to="/about" onClick={onNavigate}>{t('nav.about')}</NavLink>
                <NavLink className={linkClass} to="/blog" onClick={onNavigate}>{t('nav.blog')}</NavLink>
                <NavLink className={linkClass} to="/contact" onClick={onNavigate}>{t('nav.contact')}</NavLink>
                <Link
                    className="text-white bg-primary hover:bg-primary-hover inline-flex items-center justify-center px-6 py-2.5 shadow-lg rounded-lg font-semibold text-sm mt-2 transition-colors"
                    to="/request-demo"
                    onClick={onNavigate}
                >
                    {t('nav.demo')}
                </Link>
            </>
        );
    }

    return (
        <>
            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => setSolutionsOpen(!solutionsOpen)}
                    className="px-2 py-2 font-bold text-gray-600 hover:text-primary inline-flex items-center transition-colors duration-200"
                    aria-expanded={solutionsOpen}
                    aria-haspopup="true"
                >
                    {t('nav.solutions')}
                    <svg className={`w-4 h-4 ml-1 transition-transform ${solutionsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {solutionsOpen && (
                    <div className="absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50">
                        <Link
                            to="/solutions"
                            onClick={() => setSolutionsOpen(false)}
                            className="block px-4 py-2.5 text-primary font-bold text-sm border-b border-gray-100 hover:bg-gray-50"
                        >
                            {t('common.all_solutions')}
                        </Link>
                        {solutionLinks}
                    </div>
                )}
            </div>
            <NavLink className={linkClass} to="/technology">{t('nav.technology')}</NavLink>
            <NavLink className={linkClass} to="/industries">{t('nav.industries')}</NavLink>
            <NavLink className={linkClass} to="/case-studies">{t('nav.case_studies')}</NavLink>
            <NavLink className={linkClass} to="/about">{t('nav.about')}</NavLink>
            <NavLink className={linkClass} to="/blog">{t('nav.blog')}</NavLink>
            <NavLink className={linkClass} to="/contact">{t('nav.contact')}</NavLink>
            <Link
                className="text-white bg-primary hover:bg-primary-hover inline-flex items-center justify-center px-4 py-2.5 shadow-lg rounded-lg font-semibold text-sm ml-2 transition-colors whitespace-nowrap"
                to="/request-demo"
            >
                {t('nav.demo')}
            </Link>
        </>
    );
};

export default NavLinks;
